import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Card, Col, Input, InputNumber, Row, Tooltip, Popover, Button } from 'antd';
import { useTranslation, withTranslation } from 'react-i18next';
import { ButtonGroup } from 'react-rainbow-components';
import styled from 'styled-components';
import { debounce } from 'underscore';
import { overLayTextDetails } from '../PageNumber/pageNumberHelper';
import EditorElement from '../../../../components/EditorElement';
import ColorPicker from '../../../../artboard/ColorPicker';
import { SizeButton, SizeContainer } from '../../../SidebarMenu/styles';
import { fontSizes } from '../../TextBlockToolbar';
import { rgb2Hex, hexToRgbArr, deepCopy } from '../../../../utils/javascript';
import { useDocumentContext, COMPLEX_DOCUMENT_CONTEXT_KEYS } from '../../../../context/complexDocumentContext';
import FontSideBar from '../../FontSideBar';
import { sanitizeFontSize } from '../../../../utils/helper';
import { TEXT_OVERLAY } from './translationValues';

const NAMESPACES = ['TEXT_OVERLAY'];

const OverlayColorPickerContainer = styled.span`
  height: 16px;
  width: 16px;
  position: relative;
  cursor: pointer;
  margin-top: 10px;
  display: inline-block;
  > div {
    position: absolute;
    z-index: 11;
    margin-top: 20px;
    /* Use right positioning instead of negative margin to prevent overflow */
    right: 0;
    max-width: 100vw;
    overflow: visible;
  }
`;

const OverlayColorPicker = ({ fontColor, onColorChange }) => {
  const [showPicker, setShowPicker] = useState(false);
  const colorPickerContainerRef = useRef(null);

  useLayoutEffect(() => {
    if (!showPicker || !colorPickerContainerRef.current) return;
    const rect = colorPickerContainerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    if (rect.bottom > viewportHeight) {
      colorPickerContainerRef.current.style.transform = 'translate(0px, -390px)';
    }

    if (rect.right > viewportWidth) {
      colorPickerContainerRef.current.style.transform = `translateX(${viewportWidth - rect.right - 10}px)`;
    }
  }, [showPicker]);

  return (
    <OverlayColorPickerContainer
      style={{
        backgroundColor: rgb2Hex(fontColor),
      }}
      onClick={() => setShowPicker(true)}
    >
      {showPicker && (
        <div ref={colorPickerContainerRef} style={{ height: '366px' }}>
          <ColorPicker
            onChange={color => onColorChange(color, fontColor)}
            onBlur={() => setShowPicker(false)}
            color={rgb2Hex(fontColor)}
            qlEditor={true}
          />
        </div>
      )}
    </OverlayColorPickerContainer>
  );
};

const OverLayText = ({ uuid }) => {
  const { t } = useTranslation(NAMESPACES);
const element = overLayTextDetails().map(el => ({
    ...el,
    currentPageUuid: uuid,
}));
  const contextSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.STYLES], []);
  const [DocumentStyles, methods] = useDocumentContext(contextSelector);

  const flowSelector = useCallback(store => store[uuid], [uuid]);
  const [flow] = useDocumentContext(flowSelector, `OverlaySettings`);
  const overLayDetails = flow['overlayText'];

  const [fontFamily, setFontFamily] = useState(
    overLayDetails?.contents.style.fontFamily
      ? overLayDetails?.contents.style.fontFamily
      : DocumentStyles.fontFamily
  );
  const [defaultStyle, setDefaultStyle] = useState({
    fontSize: overLayDetails?.contents.style.fontSize
      ? overLayDetails?.contents.style.fontSize
      : DocumentStyles.fontSize,
  });
  const [fontListVisible, setFontListVisible] = useState(false);
  const inputRef = useRef();
  const hiddenRef = useRef();
  const [text, setText] = useState(
    overLayDetails?.contents?.contents[0]?.content
      ? overLayDetails.contents.contents[0].content
      : 'Text Overlay - Configure in page settings'
  );

  const overLayDetailsRef = useRef(overLayDetails);

  const isGeometryUpdateRef = useRef(false);

  useEffect(() => {
    overLayDetailsRef.current = overLayDetails;
    setText(overLayDetails?.contents?.contents[0]?.content);
    setDefaultStyle({
      fontSize: overLayDetails?.contents?.style?.fontSize
        ? overLayDetails?.contents?.style?.fontSize
        : DocumentStyles?.fontSize
    });
    isGeometryUpdateRef.current = false;
  }, [overLayDetails]);

  const onSizeChange = newFontSize => {
    setDefaultStyle({ fontSize: newFontSize });
    const currentDetails = overLayDetailsRef.current;
    let existingStyle = currentDetails?.contents.style;

    let measuredWidth = existingStyle?.width;
    let measuredHeight = Math.ceil(newFontSize * 1.4) + 10;

    if (hiddenRef.current) {
      hiddenRef.current.style.fontSize = `${newFontSize}px`;
      const rect = hiddenRef.current.getBoundingClientRect();
      measuredWidth = Math.ceil(rect.width) + 30;
    }

    const updatedContents = {
      ...currentDetails?.contents,
      style: {
        ...existingStyle,
        fontSize: newFontSize,
        width: measuredWidth,
        height: measuredHeight,
        x: existingStyle?.x,
        y: existingStyle?.y,
        rotate: existingStyle?.rotate,
      },
      contents: currentDetails?.contents?.contents.map((item, index) => {
        if (index === 0) {
          return {
            ...item,
            style: {
              ...item.style,
              fontSize: newFontSize,
              width: measuredWidth,
              height: measuredHeight,
            },
          };
        }
        return item;
      }),
    };

    methods.addOverlayText({
      uuid: uuid,
      block: {
        contents: updatedContents,
        pageNumberType: 1,
      },
    });
  };

  const handleKeypress = e => {
    e.stopPropagation();
    if (['Enter', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
      e.preventDefault();
      const value = parseFloat(inputRef.current.innerHTML.trim());
      if (!isNaN(value) && value >= 1 && value <= 400) {
        onSizeChange(value);
      }
    }
  };

  const handleKeyDown = e => {
    e.stopPropagation();

    const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'];

    if (allowedKeys.includes(e.key)) return;

    if (!/^[0-9.]$/.test(e.key)) {
      e.preventDefault();
      return;
    }

    const el = inputRef.current;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const current = el.innerText || '';

    const start = range.startOffset;
    const end = range.endOffset;

    const next = current.slice(0, start) + e.key + current.slice(end);

    const sanitized = sanitizeFontSize(next);

    e.preventDefault();

    if (!sanitized) return;

    el.innerText = sanitized;

    requestAnimationFrame(() => {
      const newRange = document.createRange();
      const sel = window.getSelection();

      const caretPos = Math.min(start + 1, sanitized.length);

      newRange.setStart(el.firstChild || el, caretPos);
      newRange.collapse(true);

      sel.removeAllRanges();
      sel.addRange(newRange);
    });
  };

  const handlePaste = e => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    const sanitized = sanitizeFontSize(pasted);
    inputRef.current.innerText = sanitized;

    requestAnimationFrame(() => {
      const range = document.createRange();
      const sel = window.getSelection();
      range.selectNodeContents(inputRef.current);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    });
  };
  const renderSize = () => {  
    const calculatedFontSize = !isNaN(defaultStyle.fontSize) && Math.round(defaultStyle.fontSize * 10) / 10;
    return (
      <span style={{ marginLeft: '10px' }}>
        <ButtonGroup
          className="fontSize-button"
          style={{ border: '1px solid #d9d9d9', borderRadius: '4px' }}
        >
          <SizeButton
            className="fontSize-button"
            style={{ marginLeft: '0px' }}
            comboBox={1}
            isMinus={1}
            onClick={() => {
              if (!isNaN(defaultStyle.fontSize)) {
                onSizeChange(defaultStyle.fontSize - 1);
              }
            }}
            radiusLeft={true}
            icon="minus"
          />
          <Popover
            content={
              <SizeContainer>
                <ul>
                  {fontSizes.map(data => (
                    <li
                      key={data}
                      onClick={e => {
                        onSizeChange(parseInt(data?.replaceAll('px', '')));
                      }}
                    >
                      {data}
                    </li>
                  ))}
                </ul>
              </SizeContainer>
            }
            title={null}
            trigger="click"
          >
            <div
              ref={inputRef}
              contentEditable={true}
              suppressContentEditableWarning={true}
              className="size-ref ql-size"
              style={{
                width: '50px',
                color: '#000',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#cdcdcd',
              }}
              onKeyPress={handleKeypress}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              onBlur={e => {
                const value = parseFloat(inputRef.current.innerHTML.trim());
                if (
                  value !== defaultStyle.fontSize &&
                  !isNaN(value) &&
                  value >= 1 &&
                  value <= 400
                ) {
                  onSizeChange(value);
                }
              }}
            >
              {!isNaN(calculatedFontSize) && calculatedFontSize
                ? calculatedFontSize
                : ' '}
            </div>
          </Popover>
          <SizeButton
            className="fontSize-button"
            comboBox={1}
            isMinus={1}
            onClick={() => {
              if (!isNaN(defaultStyle.fontSize)) {
                onSizeChange(defaultStyle.fontSize + 1);
              }
            }}
            radiusLeft={false}
            icon="plus"
          />
        </ButtonGroup>
      </span>
    );
  };

  const updateColor = ({ rgb }) => {
    const currentDetails = overLayDetailsRef.current;
    let existingStyle = currentDetails?.contents.style;
    const updatedContents = {
      ...currentDetails?.contents,
      style: {
        ...existingStyle,
        color: rgb2Hex([rgb.r, rgb.g, rgb.b]),
        x: existingStyle?.x,
        y: existingStyle?.y,
        rotate: existingStyle?.rotate,
        width: existingStyle?.width,
        height: existingStyle?.height,
      },
      contents: [
        {
          ...currentDetails?.contents?.contents[0],
          style: {
            ...currentDetails?.contents?.contents[0]?.style,
            color: rgb2Hex([rgb.r, rgb.g, rgb.b]),
          },
        },
        ...currentDetails?.contents?.contents.slice(1),
      ],
    };
    methods.addOverlayText({
      uuid: uuid,
      block: {
        contents: updatedContents,
        pageNumberType: 1,
      },
    });
  };

  const onFontClick = () => {
    setFontListVisible(true);
  };

  const updateFontFamily = newFontFamily => {
    setFontFamily(newFontFamily);
    const currentDetails = overLayDetailsRef.current;
    let existingStyle = currentDetails?.contents.style;
    const updatedContents = {
      ...currentDetails?.contents,
      style: {
        ...existingStyle,
        fontFamily: newFontFamily,
        x: existingStyle?.x,
        y: existingStyle?.y,
        rotate: existingStyle?.rotate,
        width: existingStyle?.width,
        height: existingStyle?.height,
      },
    };
    methods.addOverlayText({
      uuid: uuid,
      block: {
        contents: updatedContents,
        pageNumberType: 1,
      },
    });
  };

  const closeFontList = () => {
    setFontListVisible(false);
  };

  const updatePageTextWidthNContent = (contentCopy) => {
    if (!hiddenRef.current) return;
    const currentDetails = overLayDetailsRef.current;
    if (!currentDetails) return;
    if (isGeometryUpdateRef.current) return;
    const measuredWidth = Math.ceil(hiddenRef.current.getBoundingClientRect().width) + 30;
    const updatedContentCopy = [...contentCopy];
    if (updatedContentCopy[0]) {
      updatedContentCopy[0].style = {
        ...updatedContentCopy[0].style,
        width: measuredWidth,
      };
    }

    const updatedContents = {
      ...currentDetails.contents,
      contents: updatedContentCopy,
      style: {
        ...currentDetails.contents.style,
        width: measuredWidth,
        x: currentDetails.contents.style.x,
        y: currentDetails.contents.style.y,
        rotate: currentDetails.contents.style.rotate,
        height: currentDetails.contents.style.height,
      },
    };
    methods.addOverlayText({
      uuid: uuid,
      block: {
        contents: updatedContents,
        pageNumberType: 1,
      },
    });
  };

  const debouncedUpdatePgTextWidthNContent = useCallback(
    debounce(updatePageTextWidthNContent, 300),
    [methods.addOverlayText, uuid]
  );

  const updatePageText = event => {
    const text = event.target.value;
    setText(text);
    let contents = overLayDetails?.contents?.contents;
    const contentCopy = deepCopy(contents);
    contentCopy[0].content = text;
    debouncedUpdatePgTextWidthNContent(contentCopy);
  };

  const deleteOverLay = () => {
    if (!overLayDetails) return;
    methods.deleteOverLay({ uuid: uuid });
  };

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          padding: '18px',
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <Tooltip
            placement="top"
            title={t(TEXT_OVERLAY.DRAG_AND_DROP_INFO.key, TEXT_OVERLAY.DRAG_AND_DROP_INFO.value)}
          >
            <div
              onClick={() => {
                deleteOverLay();
              }}
              style={{
                border: overLayDetails && '1px solid red',
                background: 'black',
                borderRadius: '10px',
                height: '46px',
                marginLeft: '42px',
                width: '75%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'grab',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06), 0 2px 8px rgba(0,0,0,0.25)',
              }}
            >
              <div
                style={{
                  background: 'black',
                  height: '26px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <EditorElement
                  element={element[0]}
                  clickElement={() => {}}
                  pageSettings={{}}
                  toggleDragging={() => {}}
                  setIsDragAndDropped={() => {}}
                  draggable={true}
                  type={'overlay'}
                />
              </div>
            </div>
          </Tooltip>
        </div>

        {overLayDetails && (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: '10px',
                alignItems: 'end',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <label
                  style={{
                    color: '#3c4043',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {t(TEXT_OVERLAY.LABEL_TEXT.key, TEXT_OVERLAY.LABEL_TEXT.value)}
                </label>
                <Input
                  value={text}
                  onChange={updatePageText}
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: '13px',
                    color: '#1A1A1A',
                    background: '#fff',
                    border: '1.5px solid #E8E8E8',
                    borderRadius: '8px',
                    padding: '8px 11px',
                    outline: 'none',
                    width: '100%',
                    height: '36px',
                  }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <label
                  style={{
                    color: '#3c4043',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {t(TEXT_OVERLAY.LABEL_COLOR.key, TEXT_OVERLAY.LABEL_COLOR.value)}
                </label>
                <div className="input-container" style={{ marginBottom: '12px' }}>
                  <OverlayColorPicker
                    fontColor={hexToRgbArr(overLayDetails?.contents?.style?.color || '#000000')}
                    onColorChange={updateColor}
                  />
                </div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <label
                  style={{
                    color: '#3c4043',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {t(TEXT_OVERLAY.LABEL_FONT_FAMILY.key, TEXT_OVERLAY.LABEL_FONT_FAMILY.value)}
                </label>
                <div
                  className="ql-font"
                  onMouseDown={onFontClick}
                  style={{
                    height: '34px',
                    width: '150px',
                    color: 'black',
                    border: '1px solid #d9d9d9',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    padding: '8px',
                    justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <span
                    style={{
                      width: '170px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      color: 'black',
                      fontSize: '14px',
                      textAlign: 'center',
                    }}
                  >
                    {fontFamily}{' '}
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <label
                  style={{
                    color: '#3c4043',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {t(TEXT_OVERLAY.LABEL_FONT_SIZE.key, TEXT_OVERLAY.LABEL_FONT_SIZE.value)}
                </label>
                {renderSize()}
              </div>
            </div>
          </>
        )}
      </div>
      <div
        style={{
          height: '1px',
          whiteSpace: 'nowrap',
          visibility: 'hidden',
          overflow: 'visible',
          position: 'absolute',
          ...overLayDetails?.contents?.style,
          ...overLayDetails?.contents?.contents?.[0]?.style,
          fontSize:
            overLayDetails?.contents?.style?.fontSize || DocumentStyles.fontSize,
          width: 'auto',
          minWidth: 'auto',
          maxWidth: 'none',
          display: 'inline-block',
          padding: 0,
          margin: 0,
          border: 'none',
          boxSizing: 'content-box',
        }}
        ref={hiddenRef}
      >
        {text}
      </div>

      {fontListVisible && (
        <FontSideBar
          itemUUID={undefined}
          editor={undefined}
          setFontChange={updateFontFamily}
          fontFamily={fontFamily}
          isToolBarVisible={fontListVisible}
          closeFontList={closeFontList}
          isAbsolute={true}
        />
      )}
    </>
  );
};

export default withTranslation(NAMESPACES)(OverLayText);